import React, { useState, useEffect } from 'react';
import { categories } from '../data';
import { useProducts } from '../hooks/useProducts';
import ProductCard from '../components/ProductCard';
import { useAuth } from '../AuthContext';
import './HomePage.css';

const HERO_SLIDES = [
  { title: 'Farm Fresh', accent: 'Delivered Fast', sub: 'Order fresh fruits & vegetables straight from local farms. Delivered in 30 minutes!', img: 'https://images.unsplash.com/photo-1542838132-92c53300491e?w=1200&q=80', cta: '🛒 Shop Now', bg: 'linear-gradient(135deg,#0a2e1a,#1a5c30)' },
  { title: 'Organic & Healthy', accent: 'Superfoods', sub: 'Discover 100% organic produce. No pesticides, pure nutrition straight to your table.', img: 'https://images.unsplash.com/photo-1498579150354-977475b7ea0b?w=1200&q=80', cta: '🌿 Shop Organic', bg: 'linear-gradient(135deg,#1a2e0a,#3d5c1a)' },
  { title: 'Morning Dairy', accent: 'Always Fresh', sub: 'Farm-fresh dairy delivered every morning. Pure milk, paneer, yogurt & more.', img: 'https://images.unsplash.com/photo-1550583724-b2692b85b150?w=1200&q=80', cta: '🥛 Shop Dairy', bg: 'linear-gradient(135deg,#0a1a2e,#1a3d5c)' },
];

export default function HomePage({ navigate }) {
  const { user } = useAuth();
  const { products } = useProducts();
  const [slide, setSlide] = useState(0);
  const [activeCategory, setActiveCategory] = useState('fruits');
  const [userLocation, setUserLocation] = useState('Detecting...');

  useEffect(() => {
    const timer = setInterval(() => setSlide(s => (s + 1) % HERO_SLIDES.length), 5000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`);
          const data = await res.json();
          setUserLocation(data.address?.suburb || data.address?.city || 'Your Area');
        } catch { setUserLocation('Pune'); }
      }, () => setUserLocation('Pune'));
    } else { setUserLocation('Pune'); }
  }, []);

  const featured = products.filter(p => p.category === activeCategory).slice(0, 8);
  const bestSellers = products.filter(p => p.badge === 'Best Seller' || p.reviews > 200);
  const current = HERO_SLIDES[slide];

  return (
    <div className="homepage">
      {/* HERO */}
      <section className="hero" style={{ background: current.bg }}>
        <div className="hero-bg-img" style={{ backgroundImage: `url(${current.img})` }} />
        <div className="hero-overlay" />
        <div className="container hero-content">
          <div className="hero-text fade-in" key={slide}>
            <div className="hero-loc-badge">
              <span>📍</span> Delivering to <strong>{userLocation}</strong>
            </div>
            <h1 className="hero-title">
              {current.title}<br />
              <span className="hero-accent">{current.accent}</span>
            </h1>
            <p className="hero-sub">{current.sub}</p>
            <div className="hero-cta-row">
              <button className="hero-cta-btn" onClick={() => navigate('products')}>{current.cta}</button>
              {!user && (
                <button className="hero-ghost-btn" onClick={() => navigate('signup')}>Join Free →</button>
              )}
            </div>
            <div className="hero-stats">
              {[['10K+','Happy Customers'],['500+','Products'],['30 min','Delivery'],['4.9★','Rating']].map(([v,l]) => (
                <div key={l} className="hero-stat">
                  <strong>{v}</strong><span>{l}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="hero-right">
            <div className="hero-img-card">
              <img src={current.img} alt="fresh" />
              <div className="hero-float-badge badge-1">
                <span>🛵</span>
                <div><p>On the way!</p><p><strong>ETA 12 min</strong></p></div>
              </div>
              <div className="hero-float-badge badge-2">
                <span>✅</span>
                <div><p><strong>100% Fresh</strong></p><p>Guaranteed</p></div>
              </div>
            </div>
          </div>
        </div>

        {/* Slide indicators */}
        <div className="hero-dots">
          {HERO_SLIDES.map((_, i) => (
            <button key={i} className={`hero-dot ${i === slide ? 'active' : ''}`} onClick={() => setSlide(i)} />
          ))}
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="categories-section">
        <div className="container">
          <div className="section-header">
            <div>
              <h2 className="section-title">Shop by Category</h2>
              <p className="section-sub">Fresh picks across all categories</p>
            </div>
            <button className="btn-secondary" onClick={() => navigate('products')}>See All →</button>
          </div>
          <div className="categories-grid">
            {categories.filter(c => c.id !== 'all').map((cat, i) => (
              <button
                key={cat.id}
                className={`category-card ${activeCategory === cat.id ? 'cat-active' : ''}`}
                onClick={() => { setActiveCategory(cat.id); document.getElementById('featured-section')?.scrollIntoView({ behavior: 'smooth' }); }}
                style={{ '--cat-color': cat.color, animationDelay: `${i * 0.06}s` }}
              >
                <div className="cat-img-wrap">
                  <img src={cat.image} alt={cat.name} loading="lazy" />
                  <div className="cat-img-overlay" style={{ background: `${cat.color}99` }} />
                </div>
                <div className="cat-info">
                  <span className="cat-icon-big">{cat.icon}</span>
                  <p className="cat-name">{cat.name}</p>
                  <p className="cat-count">{products.filter(p => p.category === cat.id).length} items</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* PROMO BANNERS */}
      <section className="promo-section">
        <div className="container promo-grid">
          <div className="promo-card promo-big" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1619566636858-adf3ef46400b?w=700&q=80)' }}>
            <div className="promo-overlay" />
            <div className="promo-text">
              <span className="promo-tag">🌿 Limited Time</span>
              <h3>Organic Fruits</h3>
              <p>20% OFF on all organic picks</p>
              <button onClick={() => navigate('products')}>Shop Now →</button>
            </div>
          </div>
          <div className="promo-small-col">
            <div className="promo-card" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1628088062854-d1870b4553da?w=500&q=80)' }}>
              <div className="promo-overlay" />
              <div className="promo-text">
                <span className="promo-tag">🥛 Daily</span>
                <h3>Fresh Dairy</h3>
                <p>Morning delivery</p>
                <button onClick={() => navigate('products')}>Order →</button>
              </div>
            </div>
            <div className="promo-card" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1509440159596-0249088772ff?w=500&q=80)' }}>
              <div className="promo-overlay" />
              <div className="promo-text">
                <span className="promo-tag">🍞 Fresh Baked</span>
                <h3>Artisan Bakery</h3>
                <p>Baked every morning</p>
                <button onClick={() => navigate('products')}>Explore →</button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="featured-section" id="featured-section">
        <div className="container">
          <div className="section-header">
            <div>
              <h2 className="section-title">Featured Products</h2>
              <p className="section-sub">Handpicked fresh produce daily</p>
            </div>
            <button className="btn-secondary" onClick={() => navigate('products')}>View All →</button>
          </div>

          <div className="category-pills">
            {categories.filter(c => c.id !== 'all').map(cat => (
              <button
                key={cat.id}
                className={`cat-pill ${activeCategory === cat.id ? 'active' : ''}`}
                onClick={() => setActiveCategory(cat.id)}
                style={activeCategory === cat.id ? { background: cat.color, borderColor: cat.color } : {}}
              >
                {cat.icon} {cat.name}
              </button>
            ))}
          </div>

          <div className="products-grid">
            {featured.map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      </section>

      {/* BEST SELLERS */}
      <section className="bestsellers-section">
        <div className="container">
          <div className="section-header">
            <div>
              <h2 className="section-title">🔥 Best Sellers</h2>
              <p className="section-sub">Most loved by our customers</p>
            </div>
          </div>
          <div className="products-grid">
            {bestSellers.slice(0, 8).map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      </section>

      {/* TRACK SECTION */}
      <section className="track-section">
        <div className="container">
          <div className="track-card">
            <div className="track-left">
              <h2>📍 Real-time Order Tracking</h2>
              <p>Watch your delivery on a live map. Know exactly where your rider is and when your groceries arrive!</p>
              <div className="track-steps">
                {['Order Confirmed', 'Being Packed', 'Rider Picked Up', 'Out for Delivery', 'Delivered! 🎉'].map((s, i) => (
                  <div key={s} className="track-step">
                    <div className="ts-dot">{i + 1}</div>
                    <span>{s}</span>
                  </div>
                ))}
              </div>
              <button className="btn-primary" onClick={() => navigate('products')}>Order & Track →</button>
            </div>
            <div className="track-right">
              <img src="https://images.unsplash.com/photo-1526367790999-0150786686a2?w=600&q=80" alt="delivery" />
              <div className="track-badge">🛵 Live on Map</div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="why-section">
        <div className="container">
          <h2 className="section-title center-title">Why Choose FreshMart?</h2>
          <div className="why-grid">
            {[
              { icon: '⚡', title: '30-Min Express Delivery', desc: 'Lightning fast delivery to your doorstep, guaranteed.', img: 'https://images.unsplash.com/photo-1526367790999-0150786686a2?w=300&q=80' },
              { icon: '🌿', title: '100% Organic & Fresh', desc: 'Sourced directly from certified organic farms daily.', img: 'https://images.unsplash.com/photo-1498579150354-977475b7ea0b?w=300&q=80' },
              { icon: '💰', title: 'Best Price Guarantee', desc: 'We price-match competitors. Save more, every order.', img: 'https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?w=300&q=80' },
              { icon: '🔒', title: 'Safe & Secure', desc: 'Contactless delivery, hygienic packaging, safe payments.', img: 'https://images.unsplash.com/photo-1563865436874-9aef32095fad?w=300&q=80' },
            ].map(w => (
              <div className="why-card" key={w.title}>
                <div className="why-img"><img src={w.img} alt={w.title} /></div>
                <div className="why-icon">{w.icon}</div>
                <h4>{w.title}</h4>
                <p>{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="footer">
        <div className="container footer-inner">
          <div className="footer-brand">
            <div className="footer-logo">🌿 FreshMart</div>
            <p>Fresh groceries, delivered fast to your door in Pune & nearby areas.</p>
            <div className="footer-social">
              {['Facebook','Instagram','Twitter'].map(s => (
                <button key={s} className="social-btn">{s[0]}</button>
              ))}
            </div>
          </div>
          <div className="footer-col">
            <h5>Quick Links</h5>
            {['Home','Shop','Track Order','About Us','Contact'].map(l => <button key={l} className="footer-link-btn">{l}</button>)}
          </div>
          <div className="footer-col">
            <h5>Categories</h5>
            {categories.filter(c=>c.id!=='all').slice(0,5).map(c => <button key={c.id} className="footer-link-btn">{c.icon} {c.name}</button>)}
          </div>
          <div className="footer-col">
            <h5>Contact Us</h5>
            <p>📞 +91 98765 43210</p>
            <p>✉️ hello@freshmart.in</p>
            <p>📍 Pune, Maharashtra, India</p>
            <p>🕐 Mon–Sun: 7AM–10PM</p>
          </div>
        </div>
        <div className="footer-bottom">
          <p>© 2026 FreshMart · Made with 💚 in Pune, India</p>
        </div>
      </footer>
    </div>
  );
}
